import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/organisms/PageHeader';
import SystemCardGrid from '../components/organisms/SystemCardGrid';
import SearchBar from '../components/molecules/SearchBar';
import Loading from '../components/atoms/Loading';
import { DEMO_SYSTEMS, DEFAULT_PAGE_DESCRIPTION } from '../data/demoSystems';
import api from '../utils/api';

/**
 * Normalize API / parent payloads into SystemCard props.
 */
function normalizeSystems(list = []) {
  return list.map((item, index) => ({
    id: item.id ?? item._id ?? `system-${index}`,
    title: item.title ?? item.name ?? 'Untitled',
    status: item.status ?? 'inactive',
    statusLabel: item.statusLabel,
    description: item.description ?? item.templateDescription,
    descriptionHref: item.descriptionHref ?? item.url,
    departments: item.departments ?? item.departmentCount ?? 0,
    workflows: item.workflows ?? item.workflowCount ?? 0,
    fields: item.fields ?? item.fieldCount ?? 0,
    formsFilled: item.formsFilled ?? item.formFilledCount ?? item.forms_filled ?? 0,
    updatedOn: item.updatedOn ?? item.updatedAt ?? item.updated_on,
    titleAsLink: Boolean(item.titleAsLink ?? item.isLink),
  }));
}

/**
 * Complaint Management page — composes header, search, and system cards.
 *
 * Data comes from:
 * 1. `systems` prop (parent-controlled), or
 * 2. `systemsEndpoint` fetched via shared api client (parent auth/base URL), or
 * 3. demo data (dev / design preview).
 */
export default function ComplaintManagementPage({
  title = 'Complaint Management',
  description = DEFAULT_PAGE_DESCRIPTION,
  systems: systemsProp,
  systemsEndpoint,
  useDemoData = true,
  createLabel = 'Create New Form',
  onCreateForm,
  onConfigure,
  onOpen,
  onTitleClick,
  onDescriptionClick,
  onSearch,
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState(onSearch ? '' : undefined); // only track search state if parent wants to be notified
  const [fetchedSystems, setFetchedSystems] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [fetchStatus, setFetchStatus] = useState(
    systemsEndpoint && !systemsProp ? 'loading' : 'idle',
  );

  useEffect(() => {
    if (!systemsEndpoint || systemsProp) {
      return undefined;
    }

    const controller = new AbortController();

    api
      .get(systemsEndpoint, { signal: controller.signal })
      .then((response) => {
        const payload = response?.data;
        const list = Array.isArray(payload)
          ? payload
          : payload?.data ?? payload?.systems ?? payload?.results ?? [];
        setFetchedSystems(normalizeSystems(list));
        setFetchError(null);
        setFetchStatus('success');
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setFetchError(err?.message || 'Failed to load systems');
        setFetchedSystems([]);
        setFetchStatus('error');
      });

    return () => controller.abort();
  }, [systemsEndpoint, systemsProp]);

  const sourceSystems = useMemo(() => {
    if (systemsProp) return normalizeSystems(systemsProp);
    if (fetchedSystems) return fetchedSystems;
    if (useDemoData) return DEMO_SYSTEMS;
    return [];
  }, [systemsProp, fetchedSystems, useDemoData]);
  

  const filteredSystems = useMemo(() => {
    const q = search?.trim().toLowerCase();
    if (!q) return sourceSystems;
    return sourceSystems.filter((s) => {
      const haystack = `${s.title} ${s.description ?? ''} ${s.status ?? ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [sourceSystems, search]);

  const handleSearchChange = (e) => {
    const next = e.target.value;
    setSearch(next);
    onSearch?.(next);
  };

  useEffect(()=>{
    if (onSearch) {
      // handleSearchChange({ target: { value: search } });
      onSearch(search);
    }
  }, [search, onSearch]);

  const handleConfigure = (system) => {
    onConfigure?.(system);
    navigate(`/settings/${system.id ?? 'new'}?step=1`);
  };

  const handleCreate = () => {
    onCreateForm?.();
    navigate('/settings/new?step=1');
  };

  const loading = fetchStatus === 'loading';

  return (
    <div className="ca-complaint-management-page" data-testid="complaint-management-page" >
      <PageHeader
        title={title}
        description={description}
        actionLabel={createLabel}
        onAction={handleCreate}
      />

      <div className="ca-search-container">

      <SearchBar value={search} onChange={handleSearchChange} placeholder="Search" />
      </div>


      {loading ? (
        <Loading text="Loading systems" />
      ) : fetchError ? (
        <div className="ca-empty">
          <p className="ca-body">{fetchError}</p>
        </div>
      ) : (
        <SystemCardGrid
          systems={filteredSystems}
          onConfigure={handleConfigure}
          onOpen={onOpen}
          onTitleClick={onTitleClick}
          onDescriptionClick={onDescriptionClick}
          emptyMessage={search ? 'No systems match your search.' : 'No systems found.'}
        />
      )}
    </div>
  );
}
